import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listarMatriculasPendientes } from "../../../../services/matriculasService";
import { normalizeMatricula, nombreCompleto } from "../../../academico/matriculas/adapters/matriculasAdapter";
import { showToast } from "../../../../utils/showToast";
import { getSocket, initSocket } from "../../../../utils/socketClient";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeText = (value) => {
  if (!value) return "";
  return value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();
};

const isPendiente = (matricula) => {
  const saldo = Number(matricula?.saldo ?? 0);
  const estado = (matricula?.estadoPago || "").toLowerCase();
  return saldo > 0 || estado === "pendiente" || estado === "parcial";
};

const buildDeuda = (matricula) => {
  const totalAPagar = Number(matricula.totalAPagar ?? 0) || 0;
  const saldo = Number(matricula.saldo ?? 0) || 0;
  const pagado = Math.max(totalAPagar - saldo, 0);
  const estadoPago = matricula.estadoPago || (saldo > 0 ? "pendiente" : "pagado");

  return {
    id: matricula.id,
    matriculaId: matricula.id,
    codigoMatricula: matricula.codigoMatricula || "",
    estudiante: nombreCompleto(matricula),
    estudianteId: matricula.estudianteId || null,
    documento: matricula.documentoIdentidad || "",
    estadoPago,
    estadoMatricula: matricula.estadoMatricula || null,
    totalAPagar,
    saldo,
    pagado,
    promocion: matricula.promocion || "",
    curso: matricula.curso || "",
    fechaMatricula: matricula.fechaMatricula || null,
  };
};

export default function usePagosCobros({ cargosCountMap = {} } = {}) {
  const [allMatriculas, setAllMatriculas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const deudas = useMemo(() => {
    const pendientes = allMatriculas
      .filter((m) => isPendiente(m) || (cargosCountMap[m.id] ?? 0) > 0)
      .map(buildDeuda);
    return pendientes.sort((a, b) => b.saldo - a.saldo);
  }, [allMatriculas, cargosCountMap]);

  const fetchDeudas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listarMatriculasPendientes();
      const arr = Array.isArray(response) ? response : (Array.isArray(response?.data) ? response.data : []);
      const normalized = arr.map(normalizeMatricula);
      setAllMatriculas(normalized);
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudieron cargar las cobranzas.";
      setError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Escalonada: esta pantalla (Caja/Pagos) ya monta varios hooks propios
    // (cobros, cargos adicionales, resumen) que disparan sus fetches al mismo
    // tiempo; un pequeno delay evita sumar otra rafaga simultanea.
    let cancelled = false;
    wait(200).then(() => {
      if (!cancelled) fetchDeudas();
    });
    return () => {
      cancelled = true;
    };
  }, [fetchDeudas]);

  const refreshTimeoutRef = useRef(null);
  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) return;
    refreshTimeoutRef.current = setTimeout(() => {
      refreshTimeoutRef.current = null;
      fetchDeudas();
    }, 350);
  }, [fetchDeudas]);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    const socket = getSocket() || initSocket(token);
    if (!socket) return undefined;

    const onMatriculasChanged = () => scheduleRefresh();
    const onPagosChanged = () => scheduleRefresh();

    socket.on("matriculas:actualizadas", onMatriculasChanged);
    socket.on("pagos:actualizados", onPagosChanged);

    return () => {
      socket.off("matriculas:actualizadas", onMatriculasChanged);
      socket.off("pagos:actualizados", onPagosChanged);
    };
  }, [scheduleRefresh]);

  const deudasFiltradas = useMemo(() => {
    const term = normalizeText(search);
    if (!term) return deudas;
    const terms = term.split(/\s+/).filter(Boolean);

    return deudas.filter((item) => {
      const nombre = normalizeText(item.estudiante);
      const documento = normalizeText(item.documento);
      const codigo = normalizeText(item.codigoMatricula || item.matriculaId);
      const base = `${nombre} ${documento} ${codigo}`.trim();
      return terms.every((t) => base.includes(t));
    });
  }, [deudas, search]);

  const total = deudasFiltradas.length;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  useEffect(() => {
    setPage((prev) => Math.min(Math.max(prev, 1), totalPages));
  }, [totalPages]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const deudasPaginadas = useMemo(() => {
    const start = (page - 1) * pageSize;
    return deudasFiltradas.slice(start, start + pageSize);
  }, [deudasFiltradas, page, pageSize]);

  const goToPage = useCallback(
    (nextPage) => {
      const safe = Math.min(totalPages, Math.max(1, nextPage));
      setPage(safe);
    },
    [totalPages]
  );

  const resumen = useMemo(() => {
    return deudas.reduce(
      (acc, item) => {
        acc.total += 1;
        acc.saldo += item.saldo || 0;
        if (item.estadoPago === "pendiente") acc.pendientes += 1;
        if (item.estadoPago === "parcial") acc.parciales += 1;
        return acc;
      },
      { total: 0, saldo: 0, pendientes: 0, parciales: 0 }
    );
  }, [deudas]);

  return {
    deudas,
    deudasFiltradas,
    deudasPaginadas,
    loading,
    error,
    lastUpdated,
    resumen,
    search,
    setSearch,
    page,
    pageSize,
    setPageSize,
    totalPages,
    total,
    goToPage,
    reload: fetchDeudas,
  };
}
